/**
 * Project terrain settings shared by the 2D map, SectionView, the profile panel and the
 * 3D viewer (docs/DEM_TERRAIN.md): the project's DEM (from GET /api/projects/{id}/terrain),
 * the "Use project DEM for surface" toggle, and the 3D drape options. A tiny external
 * store like lib/doi-settings.js, so the map page and the 3D page see the same values
 * (client-side routing keeps module state alive). The toggles are remembered per
 * browser; the DEM link always comes from the server.
 */

const STORAGE_KEY = 'ugis.terrain-settings';
const PERSISTED = ['useDemSurface', 'drape3d', 'drapeStyle', 've3d', 'surfaceOpacity3d'];

export const DRAPE_STYLES = ['hillshade', 'elevation', 'satellite', 'streets', 'topo'];
export const BASEMAP_STYLES = ['satellite', 'streets', 'topo'];

const defaults = {
  projectId: null,
  demDatasetId: null,
  dem: null,            // summary from the terrain endpoint (units, bounds, min/max, has_hillshade)
  useDemSurface: false, // Inversion panel toggle: hang models without topography from the DEM
  drape3d: true,        // 3D viewer: draw the terrain mesh when the project has a DEM
  drapeStyle: 'hillshade', // 'hillshade' | 'elevation' | 'satellite' | 'streets' | 'topo'
  ve3d: 1,              // 3D vertical exaggeration
  surfaceOpacity3d: 0.6, // 3D terrain surface opacity: the sections hang below it and must stay visible
};

function load() {
  try {
    const raw = typeof window !== 'undefined' && window.localStorage ? window.localStorage.getItem(STORAGE_KEY) : null;
    const saved = raw ? JSON.parse(raw) : {};
    const out = { ...defaults };
    PERSISTED.forEach((k) => { if (saved && saved[k] !== undefined) out[k] = saved[k]; });
    if (!DRAPE_STYLES.includes(out.drapeStyle)) out.drapeStyle = 'hillshade';
    if (!(out.surfaceOpacity3d >= 0.1 && out.surfaceOpacity3d <= 1)) out.surfaceOpacity3d = defaults.surfaceOpacity3d;
    return out;
  } catch {
    return { ...defaults };
  }
}

let state = load();
const listeners = new Set();

export const getTerrainSettings = () => state;

export function setTerrainSettings(patch) {
  const next = { ...state, ...patch };
  if (Object.keys(next).every((k) => next[k] === state[k])) return;
  state = next;
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(PERSISTED.map((k) => [k, state[k]]))));
    }
  } catch { /* storage unavailable */ }
  listeners.forEach((l) => l());
}

export function subscribeTerrainSettings(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Test helper. */
export function resetTerrainSettings() {
  state = { ...defaults };
  listeners.forEach((l) => l());
}
